/**
 * Clinicians Page
 *
 * Page for managing organization members and invitations.
 * Part of the "Your Practice" feature.
 */

import { useCallback, useState } from 'react';
import { RolesGuide } from '@components/Shared/RolesGuide';
import {
  ExpandLess,
  ExpandMore,
  PersonAdd,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { InviteUserDialog } from './components/InviteUserDialog';
import { MemberTable } from './components/MemberTable';
import { OrganizationPageWrapper } from './components/OrganizationPageWrapper';
import { PendingInvitationsSection } from './components/PendingInvitationsSection';
import {
  useGetAvailableRolesQuery,
  useGetInvitationsQuery,
  useGetOrganizationMembersQuery,
  useOrganizationAccess,
  useTransformInvitations,
  useTransformMembers,
} from './store';

export default function CliniciansPage() {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [inactiveUsersExpanded, setInactiveUsersExpanded] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Organization access
  const { userIsInGroup, userIsBillingAdmin, organizationData, refetchOrg } = useOrganizationAccess();

  // API Queries
  const {
    data: membersData,
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useGetOrganizationMembersQuery(undefined, {
    skip: !userIsInGroup,
  });

  const {
    data: invitationsData,
    refetch: refetchInvitations,
  } = useGetInvitationsQuery(undefined, {
    skip: !userIsInGroup || !userIsBillingAdmin,
  });

  const { data: rolesData } = useGetAvailableRolesQuery(undefined, {
    skip: !userIsInGroup,
  });

  // Transform data
  const { activeMembers, inactiveMembers } = useTransformMembers(membersData?.members);
  const invitations = useTransformInvitations(invitationsData?.invitations);

  // Callbacks
  const handleRefreshAll = useCallback(async () => {
    try {
      await Promise.all([refetchOrg(), refetchMembers(), refetchInvitations()]);
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    }
  }, [refetchOrg, refetchMembers, refetchInvitations, enqueueSnackbar]);

  const toggleRowExpanded = useCallback((userId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleInviteSuccess = useCallback(() => {
    setInviteDialogOpen(false);
    refetchInvitations();
  }, [refetchInvitations]);

  const handleMemberChange = useCallback(() => {
    refetchMembers();
  }, [refetchMembers]);

  return (
    <OrganizationPageWrapper pageTitle="Clinicians">
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite User
        </Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshAll}>
          Refresh
        </Button>
      </Box>

      {/* Active Members Table */}
      <MemberTable
        title="Active Members"
        members={activeMembers}
        expandedRows={expandedRows}
        onToggleExpand={toggleRowExpanded}
        onMemberChange={handleMemberChange}
        availableRoles={rolesData?.roles}
      />

      {/* Inactive Members Section */}
      {inactiveMembers.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title={
              <Button
                variant="text"
                onClick={() => setInactiveUsersExpanded(!inactiveUsersExpanded)}
                startIcon={inactiveUsersExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '1rem',
                  fontWeight: 'medium',
                }}
              >
                Inactive Members ({inactiveMembers.length})
              </Button>
            }
          />
          <Collapse in={inactiveUsersExpanded}>
            <CardContent sx={{ pt: 0 }}>
              <MemberTable
                title=""
                members={inactiveMembers}
                expandedRows={expandedRows}
                onToggleExpand={toggleRowExpanded}
                onMemberChange={handleMemberChange}
                availableRoles={rolesData?.roles}
                isInactive
              />
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Roles Guide */}
      <Box sx={{ mt: 3 }}>
        <RolesGuide defaultExpanded={false} />
      </Box>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <PendingInvitationsSection
          invitations={invitations}
          onInvitationChange={refetchInvitations}
        />
      )}

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        availableRoles={rolesData?.roles}
      />
    </OrganizationPageWrapper>
  );
}
